/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCatalogId, ICookieContext, ICoreContext } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { RecentlyViewedProductItem } from './base';
import { RecentlyViewedManager } from './recently-viewed-manager';

/**
 * Represents a recently viewed product which operates a list of products and cookies to store the list.
 */
export class RecentlyViewedState extends RecentlyViewedManager<string, RecentlyViewedProductItem> {
    private static readonly _recentlyViewedLocalStorageKey: string = '_msdyn365___recently_viewed_products';

    public static instance(context: ICoreContext, maxRecentlyViewedItemsCount: number, productId: number): void {
        const cookieContext: ICookieContext = context.request.cookies;
        let items = RecentlyViewedState._retrieveItemsFromCookies(cookieContext);

        const catalogId = getCatalogId(context.request);

        const item = new RecentlyViewedProductItem(productId, catalogId);
        const max: number = maxRecentlyViewedItemsCount + 1 || 11;
        const index: number = items.map(object => object.productId).indexOf(item.productId);
        if (item && index === -1) {
            items.push(item);
        } else {
            items.splice(items.length - 1, 0, items.splice(index, 1)[0]);
        }
        if (items.length > max) {
            items = items.slice(Math.max(items.length - max, 0));
        }

        RecentlyViewedState._writeToCookie(cookieContext, RecentlyViewedState._recentlyViewedLocalStorageKey, items);
    }

    private static _retrieveItemsFromCookies(cookieContext: ICookieContext): RecentlyViewedProductItem[] {
        const items = RecentlyViewedState._readFromCookie(cookieContext, RecentlyViewedState._recentlyViewedLocalStorageKey);
        if (!ArrayExtensions.hasElements(items)) {
            return [];
        }
        return items;
    }

    private static _readFromCookie(cookieContext: ICookieContext, cookieKey: string): RecentlyViewedProductItem[] | undefined | null {
        if (!cookieKey) {
            return undefined;
        }

        if (!cookieContext.isConsentGiven()) {
            return undefined;
        }

        const cookieValue = cookieContext.get<RecentlyViewedProductItem[] | undefined | null>(cookieKey);

        return cookieValue.value;
    }

    private static _writeToCookie(cookieContext: ICookieContext, cookieKey: string, value: RecentlyViewedProductItem[]): void {
        if (!cookieKey) {
            return;
        }

        cookieContext.set(cookieKey, value, { secure: true, maxAge: 5_184_000 });
    }
}
